import styled from "@emotion/styled";
import { FocusScope } from "react-aria";

import { PROACTIVE_PLAN_FEATURES } from "@/components/modal/paywall/plan-features";
import {
  MDSButton,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";

export type ProactivePlanSheetEntrance = "trial" | "browse";

export interface ProactivePlanSheetTimings {
  sheetEasing: string;
  sheetScrimMs: number;
  sheetSlideMs: number;
}

interface ProactivePlanSheetProps {
  entrance: ProactivePlanSheetEntrance;
  isCheckoutLoading: boolean;
  isVisible: boolean;
  timings: ProactivePlanSheetTimings;
  onClose: () => void;
  onContinueToCheckout: () => void;
}

/** Shared plan receipt for both intent-specific Proactive pitches. */
export const ProactivePlanSheet = ({
  entrance,
  isCheckoutLoading,
  isVisible,
  timings,
  onClose,
  onContinueToCheckout,
}: ProactivePlanSheetProps) => (
  <FocusScope contain autoFocus>
    <PlanSheetOverlay
      $isVisible={isVisible}
      $transitionMs={timings.sheetScrimMs}
      onClick={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      onKeyDown={event => {
        if (event.key !== "Escape" || event.defaultPrevented) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        onClose();
      }}
    >
      <PlanSheet
        aria-labelledby="proactive-plan-sheet-title"
        aria-modal="true"
        role="dialog"
        tabIndex={-1}
        $easing={timings.sheetEasing}
        $isVisible={isVisible}
        $transitionMs={timings.sheetSlideMs}
        onClick={event => event.stopPropagation()}
      >
        <PlanSheetHeader>
          <div>
            <SheetEyebrow variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
              {entrance === "trial" ? "Your trial starts" : "What you get with"}
            </SheetEyebrow>
            <SheetTitle
              id="proactive-plan-sheet-title"
              variant={MDSTextVariant.HeaderM}
              weight={MDSTextWeight.SemiBold}
            >
              Mem Proactive
            </SheetTitle>
          </div>
          <MDSIconButton
            aria-label="Close plan details"
            autoFocus
            iconKind={MDSIconKind.Exit}
            size={MDSIconButtonSize.Small}
            variant={MDSIconButtonVariant.Transparent}
            onPress={onClose}
          />
        </PlanSheetHeader>

        <FeatureList>
          {PROACTIVE_PLAN_FEATURES.map(feature => (
            <Feature key={feature}>
              <FeatureIcon aria-hidden="true" kind={MDSIconKind.Check} size={13} />
              <span>{feature}</span>
            </Feature>
          ))}
        </FeatureList>

        <TermsRow>
          <MDSText variant={MDSTextVariant.TextS}>7 days free · then $99/month</MDSText>
        </TermsRow>

        <SheetButton
          isLoading={isCheckoutLoading}
          size={MDSButtonSize.Large}
          variant={MDSButtonVariant.Emphasis}
          onPress={onContinueToCheckout}
        >
          Start your 7-day free trial
          <MDSIcon
            aria-hidden="true"
            kind={MDSIconKind.RightArrow}
            size={13}
            slot={MDSButtonSlotName.RightIcon}
          />
        </SheetButton>
        <SheetFinePrint variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
          Cancel anytime. 30-day money-back guarantee—just ask.
        </SheetFinePrint>
      </PlanSheet>
    </PlanSheetOverlay>
  </FocusScope>
);

const PlanSheetOverlay = styled.div<{ $isVisible: boolean; $transitionMs: number }>`
  position: absolute;
  z-index: 2;
  inset: 0;
  border-radius: inherit;
  background: rgb(0 0 0 / 45%);
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const PlanSheet = styled.div<{
  $easing: string;
  $isVisible: boolean;
  $transitionMs: number;
}>`
  position: absolute;
  right: 12%;
  bottom: 0;
  left: 12%;
  max-height: calc(100% - 40px);
  overflow-y: auto;
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[7]} ${theme.spacing[7]}`};
  transform: ${({ $isVisible }) => ($isVisible ? "translateY(0)" : "translateY(105%)")};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-bottom: none;
  border-radius: ${({ theme }) => `${theme.borderRadius.xlarge} ${theme.borderRadius.xlarge} 0 0`};
  outline: none;
  background: ${({ theme }) => theme.colors.surface.modal};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  transition: transform ${({ $transitionMs }) => $transitionMs}ms ${({ $easing }) => $easing};

  @media (max-width: 760px) {
    right: 0;
    left: 0;
    max-height: calc(100% - 16px);
    padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[5]} ${theme.spacing[6]}`};
  }
`;

const PlanSheetHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SheetEyebrow = styled(MDSText)`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const SheetTitle = styled(MDSText)`
  display: block;
  color: ${({ theme }) => theme.colors.text.contrast};
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => `${theme.spacing[3]} 0 ${theme.spacing[1]}`};
`;

const Feature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: 5px 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
`;

const FeatureIcon = styled(MDSIcon)`
  flex: none;
  margin-top: 2px;

  .mds-icon {
    color: ${({ theme }) => theme.colors.icon.emphasis};
  }
`;

const TermsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: ${({ theme }) => `${theme.spacing[3]} 0 ${theme.spacing[4]}`};
  padding-top: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const SheetButton = styled(MDSButton)`
  width: 100%;
  min-height: 44px;

  && .mds-icon {
    color: ${({ theme }) => theme.colors.text.onAccent};
  }
`;

const SheetFinePrint = styled(MDSText)`
  display: block;
  margin-top: ${({ theme }) => theme.spacing[2]};
  text-align: center;
`;
